import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Home, ChefHat, Bed, Sofa, Bath, Briefcase, UtensilsCrossed, Check, Calculator, X, Send } from 'lucide-react';

const WHATSAPP_NUMBER = '918880331224';

// BHK Options
const bhkOptions = [
  { id: '1bhk', label: '1 BHK', basePrice: 350000 },
  { id: '2bhk', label: '2 BHK', basePrice: 450000 },
  { id: '3bhk', label: '3 BHK', basePrice: 550000 },
  { id: '4bhk', label: '4 BHK', basePrice: 650000 },
  { id: '5bhk', label: '5 BHK+', basePrice: 800000 },
];

// Room Options with base prices
const roomOptions = [
  { id: 'kitchen', label: 'Kitchen', icon: ChefHat, basePrice: 180000, description: 'Modular kitchen with cabinets' },
  { id: 'living', label: 'Living Room', icon: Sofa, basePrice: 120000, description: 'TV unit, sofa, center table' },
  { id: 'master_bedroom', label: 'Master Bedroom', icon: Bed, basePrice: 150000, description: 'Wardrobe, bed, side tables' },
  { id: 'bedroom2', label: 'Bedroom 2', icon: Bed, basePrice: 120000, description: 'Wardrobe, bed, study table' },
  { id: 'bedroom3', label: 'Bedroom 3', icon: Bed, basePrice: 100000, description: 'Wardrobe, bed' },
  { id: 'dining', label: 'Dining Area', icon: UtensilsCrossed, basePrice: 80000, description: 'Dining table, crockery unit' },
  { id: 'bathroom', label: 'Bathroom', icon: Bath, basePrice: 60000, description: 'Vanity, fixtures, tiles' },
  { id: 'home_office', label: 'Home Office', icon: Briefcase, basePrice: 90000, description: 'Desk, storage, shelves' },
];

// Package Options with multipliers
const packageOptions = [
  { 
    id: 'essential', 
    label: 'Essential', 
    multiplier: 1, 
    description: 'Quality materials, functional design',
    features: ['Laminate finishes', 'Standard hardware', 'Basic accessories', '5-year warranty'],
    color: 'from-gray-500 to-gray-600'
  },
  { 
    id: 'premium', 
    label: 'Premium', 
    multiplier: 1.4, 
    description: 'Premium materials, elegant design',
    features: ['Acrylic/PU finishes', 'Soft-close hardware', 'Designer accessories', '8-year warranty'],
    color: 'from-[#C9A646] to-[#E6C36A]'
  },
  { 
    id: 'luxe', 
    label: 'Luxe', 
    multiplier: 1.8, 
    description: 'Luxury materials, bespoke design',
    features: ['Italian finishes', 'Premium hardware', 'Luxury accessories', '10-year warranty'],
    color: 'from-purple-500 to-purple-700'
  },
];

// Size options based on BHK
const sizeRanges = {
  '1bhk': [400, 500, 600, 700],
  '2bhk': [600, 800, 1000, 1200],
  '3bhk': [1000, 1200, 1400, 1600],
  '4bhk': [1400, 1600, 1800, 2000],
  '5bhk': [1800, 2200, 2600, 3000],
};

export default function PriceCalculator() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedBhk, setSelectedBhk] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [showResult, setShowResult] = useState(false);

  // Calculate price whenever selections change
  useEffect(() => {
    if (selectedBhk && selectedRooms.length > 0 && selectedPackage) {
      let total = 0;
      const bhk = bhkOptions.find(b => b.id === selectedBhk);
      const pkg = packageOptions.find(p => p.id === selectedPackage);
      
      // Add room prices
      selectedRooms.forEach(roomId => {
        const room = roomOptions.find(r => r.id === roomId);
        if (room) {
          total += room.basePrice;
        }
      });

      // Apply size multiplier
      if (selectedSize && bhk) {
        const baseSize = sizeRanges[selectedBhk][0];
        const sizeMultiplier = selectedSize / baseSize;
        total *= sizeMultiplier;
      }

      // Apply package multiplier
      if (pkg) {
        total *= pkg.multiplier;
      }

      setEstimatedPrice(Math.round(total));
    }
  }, [selectedBhk, selectedSize, selectedRooms, selectedPackage]);

  const handleRoomToggle = (roomId) => {
    setSelectedRooms(prev => 
      prev.includes(roomId) 
        ? prev.filter(r => r !== roomId)
        : [...prev, roomId]
    );
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (showResult) {
      setShowResult(false);
    } else if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedBhk !== null;
      case 2: return selectedSize !== null;
      case 3: return selectedRooms.length > 0;
      case 4: return selectedPackage !== null;
      default: return false;
    }
  };

  const handleGetQuote = () => {
    const bhk = bhkOptions.find(b => b.id === selectedBhk);
    const pkg = packageOptions.find(p => p.id === selectedPackage);
    const rooms = selectedRooms.map(r => roomOptions.find(room => room.id === r)?.label).join(', ');
    
    const message = encodeURIComponent(
      `*Interior Design Quote Request*\n\n` +
      `*BHK Type:* ${bhk?.label}\n` +
      `*Size:* ${selectedSize} sq.ft\n` +
      `*Rooms:* ${rooms}\n` +
      `*Package:* ${pkg?.label}\n` +
      `*Estimated Budget:* ₹${estimatedPrice.toLocaleString('en-IN')}\n\n` +
      `Please provide a detailed quote for my interior project.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const resetCalculator = () => {
    setCurrentStep(1);
    setSelectedBhk(null);
    setSelectedSize(null);
    setSelectedRooms([]);
    setSelectedPackage(null);
    setEstimatedPrice(0);
    setShowResult(false);
  };

  const formatPrice = (price) => {
    if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} Lakhs`;
    }
    return `₹${price.toLocaleString('en-IN')}`;
  };

  return (
    <>
      {/* Calculator Trigger Button with Label */}
      <motion.div
        initial={{ scale: 0, x: 20 }}
        animate={{ scale: 1, x: 0 }}
        transition={{ delay: 1.5, type: 'spring', stiffness: 200 }}
        className="fixed bottom-[180px] right-6 z-50 flex items-center gap-2"
      >
        <motion.span 
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2 }}
          className="bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 shadow-lg whitespace-nowrap"
        >
          Interior Calculator
        </motion.span>
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 bg-gradient-to-r from-purple-600 to-purple-800 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/30 hover:scale-110 transition-transform"
          data-testid="calculator-trigger-btn"
        >
          <Calculator size={26} className="text-gray-900" />
        </button>
      </motion.div>

      {/* Calculator Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            data-testid="calculator-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-[#ececec] border border-gray-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-[#ececec] border-b border-gray-200 p-6 z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-2xl text-gray-900">Interior Price Calculator</h2>
                    <p className="text-gray-400 text-sm mt-1">Get an instant estimate for your project</p>
                  </div>
                  <button
                    onClick={() => { setIsOpen(false); resetCalculator(); }}
                    className="text-gray-400 hover:text-gray-900 transition-colors"
                    data-testid="calculator-close-btn"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Progress Bar */}
                {!showResult && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      {['BHK Type', 'Size', 'Rooms', 'Package'].map((step, index) => (
                        <div 
                          key={step}
                          className={`text-xs font-medium ${currentStep > index ? 'text-[#C9A646]' : currentStep === index + 1 ? 'text-gray-900' : 'text-gray-400'}`}
                        >
                          {step}
                        </div>
                      ))}
                    </div>
                    <div className="h-1 bg-white rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-[#C9A646] to-[#E6C36A]"
                        initial={{ width: 0 }}
                        animate={{ width: `${(currentStep / 4) * 100}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {!showResult ? (
                    <motion.div
                      key={currentStep}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Step 1: BHK Selection */}
                      {currentStep === 1 && (
                        <div>
                          <h3 className="text-xl text-gray-900 mb-2">Select your BHK type</h3>
                          <p className="text-gray-400 text-sm mb-6">Choose the configuration of your home</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {bhkOptions.map((bhk) => (
                              <button
                                key={bhk.id}
                                onClick={() => { setSelectedBhk(bhk.id); setSelectedSize(null); }}
                                className={`p-4 border transition-all ${
                                  selectedBhk === bhk.id
                                    ? 'border-[#C9A646] bg-[#C9A646]/10'
                                    : 'border-gray-200 hover:border-white/30'
                                }`}
                                data-testid={`bhk-${bhk.id}`}
                              >
                                <Home size={24} className={selectedBhk === bhk.id ? 'text-[#C9A646] mx-auto' : 'text-gray-400 mx-auto'} />
                                <span className={`block mt-2 font-medium ${selectedBhk === bhk.id ? 'text-[#C9A646]' : 'text-gray-900'}`}>
                                  {bhk.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 2: Size Selection */}
                      {currentStep === 2 && selectedBhk && (
                        <div>
                          <h3 className="text-xl text-gray-900 mb-2">Select your home size</h3>
                          <p className="text-gray-400 text-sm mb-6">Choose the approximate size in square feet</p>
                          <div className="grid grid-cols-2 gap-3">
                            {sizeRanges[selectedBhk]?.map((size) => (
                              <button
                                key={size}
                                onClick={() => setSelectedSize(size)}
                                className={`p-4 border transition-all ${
                                  selectedSize === size
                                    ? 'border-[#C9A646] bg-[#C9A646]/10'
                                    : 'border-gray-200 hover:border-white/30'
                                }`}
                                data-testid={`size-${size}`}
                              >
                                <span className={`block text-2xl font-heading ${selectedSize === size ? 'text-[#C9A646]' : 'text-gray-900'}`}>
                                  {size}
                                </span>
                                <span className="text-gray-400 text-sm">sq.ft</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 3: Room Selection */}
                      {currentStep === 3 && (
                        <div>
                          <h3 className="text-xl text-gray-900 mb-2">Select rooms to design</h3>
                          <p className="text-gray-400 text-sm mb-6">Choose all the rooms you want to include</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {roomOptions.map((room) => {
                              const Icon = room.icon;
                              const isSelected = selectedRooms.includes(room.id);
                              return (
                                <button
                                  key={room.id}
                                  onClick={() => handleRoomToggle(room.id)}
                                  className={`p-4 border transition-all flex items-start gap-3 text-left ${
                                    isSelected
                                      ? 'border-[#C9A646] bg-[#C9A646]/10'
                                      : 'border-gray-200 hover:border-white/30'
                                  }`}
                                  data-testid={`room-${room.id}`}
                                >
                                  <div className={`w-10 h-10 flex items-center justify-center rounded ${isSelected ? 'bg-[#C9A646]' : 'bg-white'}`}>
                                    <Icon size={20} className={isSelected ? 'text-[#0A0A0A]' : 'text-gray-400'} />
                                  </div>
                                  <div className="flex-1">
                                    <span className={`block font-medium ${isSelected ? 'text-[#C9A646]' : 'text-gray-900'}`}>
                                      {room.label}
                                    </span>
                                    <span className="text-gray-400 text-xs">{room.description}</span>
                                  </div>
                                  {isSelected && (
                                    <Check size={20} className="text-[#C9A646]" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Step 4: Package Selection */}
                      {currentStep === 4 && (
                        <div>
                          <h3 className="text-xl text-gray-900 mb-2">Choose your package</h3>
                          <p className="text-gray-400 text-sm mb-6">Select based on your preference and budget</p>
                          <div className="space-y-4">
                            {packageOptions.map((pkg) => (
                              <button
                                key={pkg.id}
                                onClick={() => setSelectedPackage(pkg.id)}
                                className={`w-full p-5 border transition-all text-left ${
                                  selectedPackage === pkg.id
                                    ? 'border-[#C9A646] bg-[#C9A646]/10'
                                    : 'border-gray-200 hover:border-white/30'
                                }`}
                                data-testid={`package-${pkg.id}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${pkg.color}`} />
                                    <span className={`text-lg font-medium ${selectedPackage === pkg.id ? 'text-[#C9A646]' : 'text-gray-900'}`}>
                                      {pkg.label}
                                    </span>
                                  </div>
                                  {selectedPackage === pkg.id && <Check size={20} className="text-[#C9A646]" />}
                                </div>
                                <p className="text-gray-400 text-sm mb-3">{pkg.description}</p>
                                <div className="flex flex-wrap gap-2">
                                  {pkg.features.map((feature) => (
                                    <span key={feature} className="text-xs px-2 py-1 bg-white text-gray-400 rounded">
                                      {feature}
                                    </span>
                                  ))}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    /* Result Screen */
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center"
                    >
                      <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-[#C9A646] to-[#E6C36A] rounded-full flex items-center justify-center">
                        <Calculator size={36} className="text-[#0A0A0A]" />
                      </div>
                      <h3 className="text-xl text-gray-900 mb-2">Your Estimated Budget</h3>
                      <p className="text-gray-400 text-sm mb-6">Based on your selections</p>
                      
                      <div className="bg-white p-6 mb-6">
                        <span className="font-heading text-5xl text-gold-gradient">
                          {formatPrice(estimatedPrice)}
                        </span>
                        <p className="text-gray-400 text-sm mt-2">*Prices are indicative and may vary</p>
                      </div>

                      {/* Summary */}
                      <div className="text-left bg-white/50 p-4 mb-6 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">BHK Type:</span>
                          <span className="text-gray-900">{bhkOptions.find(b => b.id === selectedBhk)?.label}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Size:</span>
                          <span className="text-gray-900">{selectedSize} sq.ft</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Rooms:</span>
                          <span className="text-gray-900">{selectedRooms.length} selected</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Package:</span>
                          <span className="text-gray-900">{packageOptions.find(p => p.id === selectedPackage)?.label}</span>
                        </div>
                      </div>

                      <button
                        onClick={handleGetQuote}
                        className="w-full btn-primary flex items-center justify-center gap-2 mb-3"
                        data-testid="get-quote-btn"
                      >
                        <Send size={18} />
                        Get Detailed Quote on WhatsApp
                      </button>
                      <button
                        onClick={resetCalculator}
                        className="w-full btn-secondary"
                        data-testid="recalculate-btn"
                      >
                        Recalculate
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer Navigation */}
              {!showResult && (
                <div className="sticky bottom-0 bg-[#ececec] border-t border-gray-200 p-4 flex justify-between">
                  <button
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className={`flex items-center gap-2 px-4 py-2 ${currentStep === 1 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-900 hover:text-[#C9A646]'}`}
                    data-testid="calc-back-btn"
                  >
                    <ArrowLeft size={18} />
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`flex items-center gap-2 px-6 py-2 ${
                      canProceed()
                        ? 'bg-gradient-to-r from-[#C9A646] to-[#E6C36A] text-[#0A0A0A] font-semibold'
                        : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="calc-next-btn"
                  >
                    {currentStep === 4 ? 'Get Estimate' : 'Next'}
                    <ArrowRight size={18} />
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
